"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    COLLECTES,
    FORMES_SOCIALES,
    HEBERGEURS,
    HEBERGEURS_MAXIMUM,
    OUTILS_TIERS,
    ROLES_HEBERGEMENT,
    composerConfidentialite,
    composerMentions,
    composerValidation,
    hebergeurComplet,
    valider,
    verdictCookies,
} from "@/lib/tools/legalNotice";
import { documentEnHtml, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Ecran,
    Question,
    Recommencer,
    Resultat,
    Segments,
    useParcours,
} from "./Journey";
import { useDossierOutil } from "./ToolsDossier";

/**
 * Mentions légales, politique de confidentialité et verdict cookies.
 *
 * DEUX PUBLICS, UN SEUL PARCOURS. Le solopreneur qui fait son propre site, et
 * le freelance qui livre celui d'un client. Le second répond pour son client :
 * chaque question le dit (« votre client »), les documents sont écrits au nom
 * du client, et une fiche de validation s'ajoute, que le client signe. Le
 * dossier gardé sur l'appareil ne préremplit QUE le premier cas : y verser
 * l'identité du freelance ferait publier son nom comme éditeur du site d'un
 * autre.
 *
 * LES DOCUMENTS PARTENT EN HTML. Ils finissent sur une page web, pas dans un
 * classeur : le bouton principal copie leur code HTML nu, prêt à coller dans
 * un constructeur de pages ou un composant. Le PDF reste, pour l'archive et
 * pour la fiche de validation, qui se signe.
 *
 * Le calcul et l'écriture vivent dans lib/tools/legalNotice.js ; ce fichier ne
 * fait que poser les questions et montrer ce qui en sort.
 */

const L = {
    fr: {
        phases: ["Le site", "L'éditeur", "L'hébergement", "Les données", "Les documents"],
        retour: "Retour",
        continuer: "Continuer",
        voirDocuments: "Voir mes documents",
        prive: "Tout se fait sur votre appareil : les documents s'y fabriquent, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",

        pourQuiTitre: "Pour quel site préparez-vous ces documents ?",
        pourQuiAide: "Si c'est pour un client, les documents seront écrits à son nom : c'est lui l'éditeur du site, et c'est lui qui en répond.",
        pourMoi: "Mon propre site",
        pourMoiDetail: "Mon activité, mon portfolio, ma boutique",
        pourClient: "Le site d'un client",
        pourClientDetail: "Je le construis ou je le refonds pour lui",

        siteTitre: "Comment s'appelle le site ?",
        siteTitreClient: "Comment s'appelle le site de votre client ?",
        siteAide: "Le nom sous lequel les visiteurs le connaissent, et son adresse.",
        champNomSite: "Nom du site",
        champUrl: "Adresse du site",
        aideUrl: "Par exemple monsite.fr, sans « https:// ».",

        formeTitre: "Qui édite le site ?",
        formeTitreClient: "Sous quelle forme votre client exerce-t-il ?",
        formeAide: "L'éditeur est celui qui publie le site et répond de son contenu. Sa forme décide de ce que les mentions doivent porter.",
        formeEi: "Un entrepreneur individuel",
        formeEiDetail: "Micro-entreprise, ou entreprise individuelle au réel",
        formeSociete: "Une société",
        formeSocieteDetail: "EURL, SASU, SARL, SAS…",
        formeAssociation: "Une association",
        formeAssociationDetail: "Déclarée en préfecture",

        eiTitre: "Votre entreprise",
        eiTitreClient: "L'entreprise de votre client",
        eiAide: "Le nom de l'entrepreneur, tel qu'il figure au registre. « EI » s'y ajoutera tout seul : la loi l'impose à côté du nom.",
        champPrenoms: "Prénoms",
        champNom: "Nom",
        champNomCommercial: "Nom commercial",
        aideNomCommercial: "Facultatif. La marque sous laquelle l'activité est connue, s'il y en a une.",
        activiteLibelle: "L'activité",
        activiteCommerciale: "Commerciale",
        activiteArtisanale: "Artisanale",
        activiteLiberale: "Libérale",
        aideActivite: "Libérale : conseil, formation, création, développement. Commerciale : vente de marchandises, restauration, hébergement. Artisanale : fabrication, réparation, bâtiment.",
        champSiren: "SIREN ou SIRET",
        aideSiren: "Sur votre avis de situation INSEE. Le SIRET marche aussi : on garde les neuf premiers chiffres.",
        aideSirenClient: "Sur l'avis de situation INSEE de votre client. Le SIRET marche aussi : on garde les neuf premiers chiffres.",
        champRcsVille: "Ville du greffe (RCS)",
        aideRcsVille: "La ville du tribunal de commerce où l'entreprise est immatriculée, écrite sur l'extrait Kbis.",

        societeTitre: "La société",
        societeTitreClient: "La société de votre client",
        societeAide: "Reprenez ce qui est écrit sur l'extrait Kbis.",
        champDenomination: "Dénomination sociale",
        formeSocialeLibelle: "Forme juridique",
        champCapital: "Capital social, en euros",
        champRepresentant: "Représentant légal",
        aideRepresentant: "Prénom et nom du gérant ou du président. Il sera nommé directeur de la publication.",

        associationTitre: "L'association",
        associationTitreClient: "L'association de votre client",
        associationAide: "Reprenez ce qui figure sur le récépissé de déclaration.",
        champNomAssociation: "Nom de l'association",
        champRna: "Numéro RNA",
        aideRna: "Il commence par W, suivi de neuf chiffres.",
        champSirenAssociation: "SIREN",
        aideSirenAssociation: "Facultatif. Seulement si l'association en a un.",
        champPresident: "Président ou présidente",
        aidePresident: "Prénom et nom. Il sera nommé directeur de la publication.",

        coordonneesTitre: "Comment vous joindre ?",
        coordonneesTitreClient: "Comment joindre votre client ?",
        coordonneesAide: "L'adresse déclarée au registre et un téléphone, que la loi demande, et une adresse électronique, que Service Public ajoute.",
        coordonneesAideAdresse: "Pour un entrepreneur individuel, la loi parle de son domicile. Ce qu'en disent les textes quand l'entreprise est domiciliée ailleurs est expliqué plus bas, dans les questions.",
        champVoie: "Numéro et voie",
        champComplement: "Complément d'adresse",
        aideComplement: "Facultatif. Bâtiment, étage, lieu-dit.",
        champCodePostal: "Code postal",
        champCommune: "Commune",
        champTelephone: "Téléphone",
        champEmail: "Adresse électronique",

        tvaTitre: "Facturez-vous la TVA ?",
        tvaTitreClient: "Votre client facture-t-il la TVA ?",
        tvaAide: "Un numéro de TVA intracommunautaire figure dans les mentions de qui en a un.",
        tvaFranchise: "Non, franchise en base",
        tvaFranchiseDetail: "Les factures portent « TVA non applicable »",
        tvaAssujetti: "Oui, avec un numéro",
        tvaAssujettiDetail: "Un numéro qui commence par FR",
        numeroTvaTitre: "Le numéro de TVA",
        numeroTvaAide: "Il figure sur votre espace professionnel des impôts, et sur vos factures.",
        numeroTvaAideClient: "Il figure sur les factures de votre client, et sur son espace professionnel des impôts.",
        champNumeroTva: "Numéro de TVA intracommunautaire",

        reglementeeTitre: "L'activité est-elle réglementée ?",
        reglementeeAide: "Certains métiers ont des mentions en plus : le titre et l'ordre d'une profession réglementée, ou l'autorité qui a délivré une autorisation d'exercer.",
        reglementeeNon: "Non",
        reglementeeNonDetail: "C'est le cas de la plupart des activités",
        reglementeeProfession: "Une profession réglementée",
        reglementeeProfessionDetail: "Avocat, architecte, expert-comptable, professions de santé",
        reglementeeAutorisation: "Une activité soumise à autorisation",
        reglementeeAutorisationDetail: "Carte d'agent immobilier, agrément de sécurité privée",
        autorisationTitre: "L'autorisation d'exercer",
        autorisationAide: "Service Public demande le nom et l'adresse de l'autorité qui l'a délivrée.",
        champAutorite: "Autorité qui a délivré l'autorisation",
        aideAutorite: "Par exemple la CCI pour une carte d'agent immobilier.",
        champAdresseAutorite: "Adresse de cette autorité",
        professionTitre: "La profession réglementée",
        professionAide: "Le titre exact, l'ordre ou l'organisme auprès duquel il est inscrit, et les règles qui l'encadrent.",
        champProfession: "Profession",
        champOrdre: "Ordre ou organisme professionnel",
        champPaysTitre: "État où le titre a été obtenu",
        aidePaysTitre: "Laissez vide si c'est la France : le document l'écrira.",
        champRegles: "Règles professionnelles",
        aideRegles: "Par exemple le code de déontologie de la profession, et où le lire.",

        hebergeursTitre: "Où le site est-il hébergé ?",
        hebergeursAide: "Cochez chaque service qui fait tourner une partie du site : l'interface chez l'un et le serveur chez un autre, c'est courant. Chacun sera nommé.",
        autreHebergeur: "Un autre hébergeur",
        maximumAtteint: "Quatre hébergeurs au plus.",
        rolesTitre: "Que fait chacun ?",
        rolesAide: "Les mentions disent ce que chaque hébergeur porte : l'interface, le serveur, la base de données ou les fichiers.",
        completerTitre: "Ce qu'il nous manque sur l'hébergement",
        completerAide: "La loi demande le nom, l'adresse et le téléphone de l'hébergeur. Ce que l'hébergeur ne publie pas lui-même, nous ne l'inventons pas : vous le trouverez dans son contrat ou en le lui demandant.",
        champRaisonSociale: "Raison sociale",
        champAdresseHebergeur: "Adresse postale",
        champTelephoneHebergeur: "Téléphone",
        champPaysHebergeur: "Pays",
        horsUeLibelle: "Établi hors de l'Union européenne ?",
        horsUeNon: "Non",
        horsUeOui: "Oui",
        telephoneNonPublie: "{nom} ne publie pas de numéro de téléphone.",
        telephoneInconnu: "Je n'ai pas pu obtenir ce numéro",
        telephoneInconnuDetail: "Les mentions écriront « non communiqué par l'hébergeur ». La loi demande ce numéro : réclamez-le à l'hébergeur et ajoutez-le dès que vous l'avez.",

        collectesTitre: "Que peut-on faire sur le site ?",
        collectesTitreClient: "Que peut-on faire sur le site de votre client ?",
        collectesAide: "Chaque case cochée ajoute à la politique ce qui est collecté, pourquoi et pour combien de temps. Rien de coché, c'est possible aussi.",
        collecteContact: "Écrire par un formulaire de contact",
        collecteNewsletter: "S'inscrire à une lettre d'information",
        collecteCompte: "Créer un compte",
        collecteCommandes: "Commander et payer en ligne",
        collecteRendezVous: "Prendre rendez-vous",
        collecteDiscussion: "Discuter en direct dans une fenêtre de discussion",

        outilsTitre: "Quels outils le site charge-t-il ?",
        outilsAide: "Cochez tout ce qui est installé, même une simple vidéo intégrée : chacun reçoit des données de vos visiteurs, et certains imposent un bandeau.",
        aucunOutil: "Aucun de ces outils",
        autreOutil: "Un outil qui n'est pas dans la liste",
        autreOutilDetail: "Pixel TikTok, Vimeo, Hotjar, un widget d'avis…",
        ajouterOutil: "Ajouter un autre outil",
        retirerOutil: "Retirer",
        champNomOutil: "Nom de l'outil",
        champSocieteOutil: "Société qui le fournit",
        aideSocieteOutil: "Celle que nomme sa politique de confidentialité pour les clients européens.",
        champUsageOutil: "À quoi il sert",
        aideUsageOutil: "En quelques mots, par exemple « lecture des vidéos ».",
        champPaysOutil: "Pays de la société",
        traceursLibelle: "Dépose-t-il des cookies ou des traceurs ?",
        traceursOui: "Oui, ou je ne sais pas",
        traceursNon: "Non",
        categorieAudience: "Mesure d'audience",
        categoriePublicite: "Publicité",
        categorieVideo: "Vidéos et cartes",
        categorieRendezVous: "Rendez-vous",
        categoriePaiement: "Paiement",
        categorieEmailing: "Lettre d'information",
        categorieEchange: "Échanges avec les visiteurs",
        categorieTechnique: "Technique",

        reglagesTitre: "Comment l'outil est-il réglé ?",
        reglagesAide: "Ces outils peuvent se passer de consentement, mais seulement à certaines conditions, rappelées sous chacun. Dans le doute, répondez « Je ne sais pas » : on vous comptera un bandeau, c'est l'erreur la moins coûteuse.",
        reglageExempte: "Les conditions sont remplies",
        reglageStandard: "Je ne sais pas",

        contactDonneesTitre: "Où écrire pour ses données ?",
        contactDonneesAide: "L'adresse à laquelle un visiteur demande une copie de ses données ou leur effacement. Par défaut, l'adresse de contact.",
        champEmailDonnees: "Adresse pour les demandes sur les données",

        prestataireTitre: "Et vous ?",
        prestataireAide: "Votre client signera une fiche qui dit qu'il a fourni les informations et relu les documents. Elle vous nomme.",
        champPrestataireNom: "Votre nom ou celui de votre entreprise",
        champPrestataireUrl: "Votre site",
        aidePrestataireUrl: "Facultatif.",
        creditLibelle: "Vous citer dans les mentions comme créateur du site ?",
        creditOui: "Oui",
        creditNon: "Non",
        signataireCiviliteLibelle: "Civilité de la personne qui signe",
        madame: "Madame",
        monsieur: "Monsieur",
        champSignataire: "Qui signe pour le client",
        aideSignataire: "Prénom et nom de la personne qui validera les documents.",
        champLieuSignature: "Lieu de signature",
        aideLieuSignature: "Facultatif. La ville où la fiche sera signée.",

        resultatTitre: "Vos documents",
        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez ce qui est signalé pour obtenir vos documents.",
        verifierTitre: "Une valeur à vérifier",
        motifFormat: "le format n'est pas reconnu",
        motifCle: "un chiffre semble faux, la clé de contrôle ne correspond pas",

        cookiesBandeauTitre: "Il vous faut un bandeau de consentement",
        cookiesSansBandeauTitre: "Pas besoin de bandeau",
        cookiesBandeau: "{outils} déposent des traceurs qui demandent l'accord du visiteur. Rien ne doit être chargé avant son choix.",
        cookiesBandeauUn: "{outils} dépose des traceurs qui demandent l'accord du visiteur. Rien ne doit être chargé avant son choix.",
        cookiesSansBandeau: "Le site ne charge rien qui demande l'accord du visiteur. La politique de confidentialité le dit, et c'est suffisant.",
        cookiesRegle1: "Refuser doit être aussi simple qu'accepter : la CNIL recommande un bouton « Tout refuser » à côté de « Tout accepter ».",
        cookiesRegle2: "Tant que le visiteur n'a pas choisi, aucun traceur soumis à son accord n'est déposé, et la vidéo ou la carte reste un aperçu à cliquer.",
        cookiesRegle3: "Un lien en bas de chaque page permet de changer d'avis.",
        cookiesRegle4: "Un refus se respecte : on ne redemande pas à chaque page.",
        cookiesExemptes: "Sans accord nécessaire : {outils}.",

        documentAffiche: "Le document affiché",
        docMentions: "Mentions légales",
        docConfidentialite: "Politique de confidentialité",
        docValidation: "Fiche de validation",
        docEnFrancais: "",
        copierHtml: "Copier le code HTML",
        htmlCopie: "Code copié",
        telecharger: "Télécharger en PDF",
        voirCode: "Voir le code",
        masquerCode: "Masquer le code",
        validationAide: "À faire signer par votre client avant la mise en ligne. Il peut signer ici, ou à la main après impression.",
        signatureClient: "Signature de votre client",
        signatureClientAide: "Facultative : votre client peut aussi signer à la main sur le papier imprimé.",
        effacer: "Effacer",

        alerteTvaAutreSiren: "Le numéro de TVA ne correspond pas au SIREN saisi. Vérifiez l'un ou l'autre.",
        alerteRoleSiteEtAutres: "Un hébergeur porte « tout le site » alors que d'autres sont déclarés. Précisez ce que fait chacun.",
        alerteRoleEnDouble: "Deux hébergeurs portent le même rôle. Si c'est voulu (deux bases de données, par exemple), tout va bien.",
        alertePaiementSansCommandes: "Un outil de paiement est coché, mais pas la commande en ligne : la politique n'en dira rien.",
        alerteRendezVousSansCollecte: "Un outil de rendez-vous est coché, mais pas la prise de rendez-vous : la politique n'en dira rien.",
        alerteEmailingSansNewsletter: "Un outil d'envoi est coché, mais pas l'inscription à une lettre : la politique n'en dira rien.",
        alerteDiscussionSansCollecte: "Un outil de discussion est coché, mais pas la discussion en direct : la politique ne décrira pas les messages reçus.",
        alerteTelephoneHebergeurInconnu: "Il manque le téléphone d'un hébergeur, que la loi demande. Les mentions disent qu'il n'est pas communiqué : ajoutez-le dès que vous l'obtenez.",

        suiteTitre: "Et maintenant",
        suite1: "Créez deux pages, « Mentions légales » et « Politique de confidentialité », et collez-y le code de chacune.",
        suite2: "Mettez un lien vers les deux en bas de chaque page du site : un visiteur doit les trouver sans chercher.",
        suite3Bandeau: "Installez le bandeau avant d'ouvrir le site, et vérifiez dans l'inspecteur du navigateur qu'aucun cookie soumis à l'accord n'apparaît avant le clic.",
        suite3SansBandeau: "Repassez par ici le jour où vous ajoutez un outil : une vidéo intégrée ou une mesure d'audience peuvent suffire à rendre le bandeau obligatoire.",
        suite4Client: "Faites signer la fiche de validation par votre client, et gardez-la avec le devis.",
        suite5Client: "Ajoutez à vos devis une clause qui dit que le client fournit et valide le contenu de ses documents juridiques, et que vous n'assurez aucun conseil juridique.",
        fondTitre: "Ce que ces documents ne font pas",
        fond: "Ils ne remplacent pas des conditions générales de vente : si le site vend à des particuliers, il en faut, et elles dépendent de ce qui est vendu. Ils ne disent rien non plus d'un traitement de données que le site ne fait pas voir, comme un fichier de clients tenu à part.",
        recommencer: "Recommencer",
    },
    en: {
        phases: ["The site", "The publisher", "Hosting", "The data", "The documents"],
        retour: "Back",
        continuer: "Continue",
        voirDocuments: "See my documents",
        prive: "Everything happens on your device: the documents are built there, nobody receives your answers, not even us, and it all disappears with the tab.",

        pourQuiTitre: "Which site are you preparing these documents for?",
        pourQuiAide: "If it is for a client, the documents will be written in their name: they publish the site, and they answer for it.",
        pourMoi: "My own site",
        pourMoiDetail: "My business, my portfolio, my shop",
        pourClient: "A client's site",
        pourClientDetail: "I am building or redesigning it for them",

        siteTitre: "What is the site called?",
        siteTitreClient: "What is your client's site called?",
        siteAide: "The name visitors know it by, and its address.",
        champNomSite: "Site name",
        champUrl: "Site address",
        aideUrl: "For example mysite.com, without « https:// ».",

        formeTitre: "Who publishes the site?",
        formeTitreClient: "What kind of business is your client?",
        formeAide: "The publisher is whoever puts the site online and answers for its content. Its legal form decides what the notice must carry.",
        formeEi: "A sole trader",
        formeEiDetail: "Micro-enterprise, or sole trader under the standard regime",
        formeSociete: "A company",
        formeSocieteDetail: "EURL, SASU, SARL, SAS…",
        formeAssociation: "An association",
        formeAssociationDetail: "Declared to the prefecture",

        eiTitre: "Your business",
        eiTitreClient: "Your client's business",
        eiAide: "The trader's name as it appears on the register. « EI » is added automatically: French law requires it next to the name.",
        champPrenoms: "Forenames",
        champNom: "Surname",
        champNomCommercial: "Trading name",
        aideNomCommercial: "Optional. The brand the business is known by, if there is one.",
        activiteLibelle: "The activity",
        activiteCommerciale: "Commercial",
        activiteArtisanale: "Craft",
        activiteLiberale: "Professional",
        aideActivite: "Professional: consulting, training, design, development. Commercial: selling goods, catering, accommodation. Craft: making, repairing, building.",
        champSiren: "SIREN or SIRET",
        aideSiren: "On your INSEE registration notice. A SIRET works too: we keep the first nine digits.",
        aideSirenClient: "On your client's INSEE registration notice. A SIRET works too: we keep the first nine digits.",
        champRcsVille: "Registry town (RCS)",
        aideRcsVille: "The town of the commercial court where the business is registered, shown on the Kbis extract.",

        societeTitre: "The company",
        societeTitreClient: "Your client's company",
        societeAide: "Copy what is written on the Kbis extract.",
        champDenomination: "Company name",
        formeSocialeLibelle: "Legal form",
        champCapital: "Share capital, in euros",
        champRepresentant: "Legal representative",
        aideRepresentant: "Forename and surname of the manager or president. They will be named publication director.",

        associationTitre: "The association",
        associationTitreClient: "Your client's association",
        associationAide: "Copy what appears on the declaration receipt.",
        champNomAssociation: "Association name",
        champRna: "RNA number",
        aideRna: "It starts with W, followed by nine digits.",
        champSirenAssociation: "SIREN",
        aideSirenAssociation: "Optional. Only if the association has one.",
        champPresident: "President",
        aidePresident: "Forename and surname. They will be named publication director.",

        coordonneesTitre: "How can you be reached?",
        coordonneesTitreClient: "How can your client be reached?",
        coordonneesAide: "The address declared to the register and a phone number, which the law asks for, and an email address, which Service Public adds.",
        coordonneesAideAdresse: "For a sole trader, the law speaks of their home address. What the texts say when the business is registered elsewhere is explained further down, in the questions.",
        champVoie: "Number and street",
        champComplement: "Address line 2",
        aideComplement: "Optional. Building, floor, locality.",
        champCodePostal: "Postcode",
        champCommune: "Town",
        champTelephone: "Phone",
        champEmail: "Email address",

        tvaTitre: "Do you charge VAT?",
        tvaTitreClient: "Does your client charge VAT?",
        tvaAide: "Whoever has an intra-EU VAT number shows it in the legal notice.",
        tvaFranchise: "No, VAT exemption",
        tvaFranchiseDetail: "Invoices say « TVA non applicable »",
        tvaAssujetti: "Yes, with a number",
        tvaAssujettiDetail: "A number starting with FR",
        numeroTvaTitre: "The VAT number",
        numeroTvaAide: "It appears in your professional tax account, and on your invoices.",
        numeroTvaAideClient: "It appears on your client's invoices, and in their professional tax account.",
        champNumeroTva: "Intra-EU VAT number",

        reglementeeTitre: "Is the activity regulated?",
        reglementeeAide: "Some trades carry extra particulars: the title and professional body of a regulated profession, or the authority that issued a licence to practise.",
        reglementeeNon: "No",
        reglementeeNonDetail: "That is the case for most activities",
        reglementeeProfession: "A regulated profession",
        reglementeeProfessionDetail: "Lawyer, architect, chartered accountant, health professions",
        reglementeeAutorisation: "A licensed activity",
        reglementeeAutorisationDetail: "Estate agent card, private security approval",
        autorisationTitre: "The licence to practise",
        autorisationAide: "Service Public asks for the name and address of the authority that issued it.",
        champAutorite: "Authority that issued the licence",
        aideAutorite: "For example the CCI for an estate agent card.",
        champAdresseAutorite: "Address of that authority",
        professionTitre: "The regulated profession",
        professionAide: "The exact title, the body it is registered with, and the rules that govern it.",
        champProfession: "Profession",
        champOrdre: "Professional body",
        champPaysTitre: "State where the title was obtained",
        aidePaysTitre: "Leave empty if it is France: the document will say so.",
        champRegles: "Professional rules",
        aideRegles: "For example the profession's code of conduct, and where to read it.",

        hebergeursTitre: "Where is the site hosted?",
        hebergeursAide: "Tick every service that runs part of the site: the front end with one and the server with another is common. Each will be named.",
        autreHebergeur: "Another host",
        maximumAtteint: "Four hosts at most.",
        rolesTitre: "What does each one do?",
        rolesAide: "The notice says what each host carries: the front end, the server, the database or the files.",
        completerTitre: "What we are missing about hosting",
        completerAide: "The law asks for the host's name, address and phone number. What a host does not publish itself, we do not make up: you will find it in its contract or by asking it.",
        champRaisonSociale: "Company name",
        champAdresseHebergeur: "Postal address",
        champTelephoneHebergeur: "Phone",
        champPaysHebergeur: "Country",
        horsUeLibelle: "Based outside the European Union?",
        horsUeNon: "No",
        horsUeOui: "Yes",
        telephoneNonPublie: "{nom} does not publish a phone number.",
        telephoneInconnu: "I could not get this number",
        telephoneInconnuDetail: "The notice will say « non communiqué par l'hébergeur ». The law asks for this number: request it from the host and add it as soon as you have it.",

        collectesTitre: "What can visitors do on the site?",
        collectesTitreClient: "What can visitors do on your client's site?",
        collectesAide: "Each ticked box adds to the policy what is collected, why and for how long. Ticking nothing is fine too.",
        collecteContact: "Write through a contact form",
        collecteNewsletter: "Sign up to a newsletter",
        collecteCompte: "Create an account",
        collecteCommandes: "Order and pay online",
        collecteRendezVous: "Book an appointment",
        collecteDiscussion: "Chat live in a chat window",

        outilsTitre: "Which tools does the site load?",
        outilsAide: "Tick everything that is installed, even a simple embedded video: each one receives data about your visitors, and some require a banner.",
        aucunOutil: "None of these tools",
        autreOutil: "A tool that is not on the list",
        autreOutilDetail: "TikTok pixel, Vimeo, Hotjar, a review widget…",
        ajouterOutil: "Add another tool",
        retirerOutil: "Remove",
        champNomOutil: "Tool name",
        champSocieteOutil: "Company that provides it",
        aideSocieteOutil: "The one its privacy policy names for European customers.",
        champUsageOutil: "What it is for",
        aideUsageOutil: "In a few words, for example « playing videos ».",
        champPaysOutil: "Company's country",
        traceursLibelle: "Does it set cookies or trackers?",
        traceursOui: "Yes, or I don't know",
        traceursNon: "No",
        categorieAudience: "Audience measurement",
        categoriePublicite: "Advertising",
        categorieVideo: "Videos and maps",
        categorieRendezVous: "Appointments",
        categoriePaiement: "Payment",
        categorieEmailing: "Newsletter",
        categorieEchange: "Talking to visitors",
        categorieTechnique: "Technical",

        reglagesTitre: "How is the tool configured?",
        reglagesAide: "These tools can do without consent, but only under certain conditions, listed under each one. If in doubt, answer « I don't know »: we will count a banner, which is the cheapest mistake.",
        reglageExempte: "The conditions are met",
        reglageStandard: "I don't know",

        contactDonneesTitre: "Where should people write about their data?",
        contactDonneesAide: "The address a visitor uses to ask for a copy of their data or for its deletion. By default, the contact address.",
        champEmailDonnees: "Address for data requests",

        prestataireTitre: "And you?",
        prestataireAide: "Your client will sign a sheet saying they supplied the information and read the documents. It names you.",
        champPrestataireNom: "Your name or your business name",
        champPrestataireUrl: "Your website",
        aidePrestataireUrl: "Optional.",
        creditLibelle: "Credit you in the notice as the site's creator?",
        creditOui: "Yes",
        creditNon: "No",
        signataireCiviliteLibelle: "Title of the person signing",
        madame: "Ms",
        monsieur: "Mr",
        champSignataire: "Who signs for the client",
        aideSignataire: "Forename and surname of the person who will approve the documents.",
        champLieuSignature: "Place of signature",
        aideLieuSignature: "Optional. The town where the sheet will be signed.",

        resultatTitre: "Your documents",
        manquesTitre: "Something is still missing",
        manquesAide: "Fill in what is flagged to get your documents.",
        verifierTitre: "Something to check",
        motifFormat: "the format is not recognised",
        motifCle: "a digit looks wrong, the check digit does not match",

        cookiesBandeauTitre: "You need a consent banner",
        cookiesSansBandeauTitre: "No banner needed",
        cookiesBandeau: "{outils} set trackers that require the visitor's consent. Nothing may load before they choose.",
        cookiesBandeauUn: "{outils} sets trackers that require the visitor's consent. Nothing may load before they choose.",
        cookiesSansBandeau: "The site loads nothing that requires the visitor's consent. The privacy policy says so, and that is enough.",
        cookiesRegle1: "Refusing must be as easy as accepting: the CNIL recommends a « Reject all » button next to « Accept all ».",
        cookiesRegle2: "Until the visitor has chosen, no tracker that needs consent is set, and the video or the map stays a preview to click.",
        cookiesRegle3: "A link at the bottom of every page lets visitors change their mind.",
        cookiesRegle4: "A refusal is respected: you do not ask again on every page.",
        cookiesExemptes: "No consent needed: {outils}.",

        documentAffiche: "The document shown",
        docMentions: "Legal notice",
        docConfidentialite: "Privacy policy",
        docValidation: "Approval sheet",
        docEnFrancais: "The documents stay in French: they meet French legal requirements and are read by French visitors and authorities.",
        copierHtml: "Copy the HTML code",
        htmlCopie: "Code copied",
        telecharger: "Download as PDF",
        voirCode: "Show the code",
        masquerCode: "Hide the code",
        validationAide: "Have your client sign it before the site goes live. They can sign here, or by hand after printing.",
        signatureClient: "Your client's signature",
        signatureClientAide: "Optional: your client can also sign by hand on the printed paper.",
        effacer: "Clear",

        alerteTvaAutreSiren: "The VAT number does not match the SIREN entered. Check one or the other.",
        alerteRoleSiteEtAutres: "One host carries « the whole site » while others are declared. Say what each one does.",
        alerteRoleEnDouble: "Two hosts carry the same role. If that is intended (two databases, for instance), all is well.",
        alertePaiementSansCommandes: "A payment tool is ticked, but not online ordering: the policy will say nothing about it.",
        alerteRendezVousSansCollecte: "A booking tool is ticked, but not appointment booking: the policy will say nothing about it.",
        alerteEmailingSansNewsletter: "A mailing tool is ticked, but not newsletter sign-up: the policy will say nothing about it.",
        alerteDiscussionSansCollecte: "A chat tool is ticked, but not live chat: the policy will not describe the messages received.",
        alerteTelephoneHebergeurInconnu: "A host's phone number is missing, and the law asks for it. The notice says it is not provided: add it as soon as you get it.",

        suiteTitre: "What happens next",
        suite1: "Create two pages, « Mentions légales » and « Politique de confidentialité », and paste each one's code into them.",
        suite2: "Link to both at the bottom of every page of the site: a visitor must find them without searching.",
        suite3Bandeau: "Install the banner before the site goes live, and check in the browser inspector that no consent-based cookie appears before the click.",
        suite3SansBandeau: "Come back here the day you add a tool: an embedded video or an audience tool can be enough to make the banner compulsory.",
        suite4Client: "Have your client sign the approval sheet, and keep it with the quotation.",
        suite5Client: "Add a clause to your quotations saying the client supplies and approves the content of their legal documents, and that you give no legal advice.",
        fondTitre: "What these documents do not do",
        fond: "They do not replace terms and conditions of sale: if the site sells to consumers, it needs them, and they depend on what is sold. Nor do they say anything about data processing the site does not show, such as a client file kept elsewhere.",
        recommencer: "Start again",
    },
};

const PHASES_PAR_ECRAN = {
    pourQui: 0,
    site: 0,
    forme: 1,
    ei: 1,
    societe: 1,
    association: 1,
    coordonnees: 1,
    tva: 1,
    numeroTva: 1,
    reglementee: 1,
    profession: 1,
    autorisation: 1,
    hebergeurs: 2,
    roles: 2,
    completer: 2,
    collectes: 3,
    outils: 3,
    reglages: 3,
    contactDonnees: 3,
    prestataire: 3,
    resultat: 4,
};

const VIDE = {
    pourQui: "",
    nomSite: "",
    url: "",
    forme: "",
    prenoms: "",
    nom: "",
    nomCommercial: "",
    activite: "",
    siren: "",
    rcsVille: "",
    denomination: "",
    formeSociale: "",
    capital: "",
    representant: "",
    nomAssociation: "",
    rna: "",
    president: "",
    voie: "",
    complement: "",
    codePostal: "",
    commune: "",
    telephone: "",
    email: "",
    tva: "",
    numeroTva: "",
    reglementee: null,
    autorite: "",
    adresseAutorite: "",
    profession: "",
    ordre: "",
    paysTitre: "",
    regles: "",
    hebergeurs: [],
    collectes: {},
    outils: {},
    reglages: {},
    autresOutils: [],
    emailDonnees: "",
    prestataireNom: "",
    prestataireUrl: "",
    creditCreateur: true,
    signataireCivilite: "",
    signataireClient: "",
    lieuDeSignature: "",
};

/** Un outil hors liste, avant qu'on le décrive : des traceurs par prudence. */
const OUTIL_VIDE = { nom: "", societe: "", usage: "", pays: "", horsUe: false, traceurs: true };
const AUTRES_OUTILS_MAXIMUM = 4;

/** L'ordre des catégories d'outils à l'écran. */
const CATEGORIES = ["audience", "publicite", "video", "rendezVous", "paiement", "emailing", "echange", "technique"];

/** Les libellés des manques : des clés de la lib, hors du dictionnaire L. */
const LIBELLES_MANQUES = {
    fr: {
        pourQui: "Pour qui sont les documents",
        forme: "La forme de l'éditeur",
        prenoms: "Les prénoms de l'entrepreneur",
        nom: "Le nom de l'entrepreneur",
        activite: "La nature de l'activité",
        rcsVille: "La ville du greffe",
        denomination: "La dénomination sociale",
        formeSociale: "La forme juridique",
        capital: "Le capital social",
        representant: "Le représentant légal",
        nomAssociation: "Le nom de l'association",
        rna: "Le numéro RNA",
        president: "Le président de l'association",
        siren: "Le SIREN",
        voie: "L'adresse",
        codePostal: "Le code postal, en cinq chiffres",
        commune: "La commune",
        email: "L'adresse électronique",
        telephone: "Le téléphone",
        tva: "La TVA",
        numeroTva: "Le numéro de TVA",
        reglementee: "Si l'activité est réglementée",
        regles: "Les règles professionnelles",
        autorite: "L'autorité qui a délivré l'autorisation",
        adresseAutorite: "L'adresse de cette autorité",
        signataireCivilite: "La civilité de la personne qui signe",
        hebergeurPays: "Le pays de l'hébergeur {n}",
        autreOutilNom: "Le nom de l'outil {n} hors liste",
        autreOutilSociete: "La société de l'outil {n} hors liste",
        autreOutilPays: "Le pays de l'outil {n} hors liste",
        profession: "La profession réglementée",
        ordre: "L'ordre ou l'organisme professionnel",
        nomSite: "Le nom du site",
        url: "L'adresse du site",
        hebergeurs: "Au moins un hébergeur",
        emailDonnees: "L'adresse pour les demandes sur les données",
        prestataireNom: "Votre nom",
        signataireClient: "Qui signe pour le client",
        hebergeurRaisonSociale: "La raison sociale de l'hébergeur {n}",
        hebergeurAdresse: "L'adresse de l'hébergeur {n}",
        hebergeurTelephone: "Le téléphone de l'hébergeur {n}",
    },
    en: {
        pourQui: "Who the documents are for",
        forme: "The publisher's legal form",
        prenoms: "The trader's forenames",
        nom: "The trader's surname",
        activite: "The kind of activity",
        rcsVille: "The registry town",
        denomination: "The company name",
        formeSociale: "The legal form",
        capital: "The share capital",
        representant: "The legal representative",
        nomAssociation: "The association's name",
        rna: "The RNA number",
        president: "The association's president",
        siren: "The SIREN",
        voie: "The address",
        codePostal: "The postcode, five digits",
        commune: "The town",
        email: "The email address",
        telephone: "The phone number",
        tva: "VAT",
        numeroTva: "The VAT number",
        reglementee: "Whether the activity is regulated",
        regles: "The professional rules",
        autorite: "The authority that issued the licence",
        adresseAutorite: "That authority's address",
        signataireCivilite: "The title of the person signing",
        hebergeurPays: "Host {n}'s country",
        autreOutilNom: "The name of unlisted tool {n}",
        autreOutilSociete: "The company of unlisted tool {n}",
        autreOutilPays: "The country of unlisted tool {n}",
        profession: "The regulated profession",
        ordre: "The professional body",
        nomSite: "The site name",
        url: "The site address",
        hebergeurs: "At least one host",
        emailDonnees: "The address for data requests",
        prestataireNom: "Your name",
        signataireClient: "Who signs for the client",
        hebergeurRaisonSociale: "Host {n}'s company name",
        hebergeurAdresse: "Host {n}'s address",
        hebergeurTelephone: "Host {n}'s phone number",
    },
};

/** « hebergeur1Telephone » se lit « Le téléphone de l'hébergeur 2 ». */
function libelleManque(lang, cle) {
    const dico = LIBELLES_MANQUES[lang] || LIBELLES_MANQUES.fr;
    const m = /^(hebergeur|autreOutil)(\d+)(\w+)$/.exec(cle);
    if (m && dico[`${m[1]}${m[3]}`]) return dico[`${m[1]}${m[3]}`].replace("{n}", String(Number(m[2]) + 1));
    return dico[cle] || cle;
}

const majuscule = (mot) => mot.charAt(0).toUpperCase() + mot.slice(1);

const remplace = (texte, valeurs) => texte.replace(/\{(\w+)\}/g, (_, k) => valeurs[k] ?? "");

/** Une liste lisible dans la langue de l'interface. */
function enumeration(mots, lang) {
    if (mots.length <= 1) return mots.join("");
    const et = lang === "en" ? " and " : " et ";
    return `${mots.slice(0, -1).join(", ")}${et}${mots[mots.length - 1]}`;
}

/** Une case à cocher, sobre : la case, son libellé, et sa précision. */
function Case({ coche, libelle, detail, onToggle, disabled = false }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-pressed={coche}
            disabled={disabled}
            className={`group w-full min-h-[48px] text-left flex items-start gap-3 px-4 py-3 rounded-xl border transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                coche
                    ? "border-primary/60 bg-primary/[0.05]"
                    : "border-gray-200/70 dark:border-white/10 hover:border-primary/40"
            }`}
        >
            <SolarIcon
                name={coche ? "check-square-bold" : "stop-linear"}
                className={`mt-0.5 text-xl shrink-0 transition-colors duration-200 ${
                    coche ? "text-primary" : "text-gray-300 dark:text-white/25"
                }`}
            />
            <span>
                <span className="block text-[15px] leading-6 dark:text-white">{libelle}</span>
                {detail && (
                    <span className="block text-xs leading-5 text-gray-500 dark:text-lightBlue/70 mt-0.5">{detail}</span>
                )}
            </span>
        </button>
    );
}

/** Un intitulé de groupe, au-dessus de ses cases. */
function Intitule({ children }) {
    return (
        <div className="text-xs font-semibold text-gray-500 dark:text-lightBlue/70 mb-2.5">{children}</div>
    );
}

/** Un encadré de conseil, le même partout dans le résultat. */
function Encadre({ icone, titre, children, ton = "neutre" }) {
    const bord = ton === "attention" ? "border-amber-500/30 bg-amber-500/[0.06]" : "border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03]";
    return (
        <div className={`rounded-2xl border ${bord} p-5`}>
            <div className="flex items-center gap-2.5 mb-3">
                <SolarIcon name={icone} className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                <div className="font-semibold dark:text-white leading-snug">{titre}</div>
            </div>
            {children}
        </div>
    );
}

export default function MentionsLegales({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    const [r, setR] = useState(VIDE);
    const [vue, setVue] = useState("mentions");
    const [copie, setCopie] = useState("");
    const [code, setCode] = useState(false);
    /* La signature du client, jamais celle de la visite : voir HostingAuthorisation.jsx. */
    const [signature, setSignature] = useState("");

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "pourQui",
        () => {
            setR(VIDE);
            setVue("mentions");
            setCopie("");
            setCode(false);
            setSignature("");
        },
        PHASES_PAR_ECRAN,
        null,
        { r, setR },
    );

    const client = r.pourQui === "client";
    const pour = (moi, autre) => (client ? autre : moi);
    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));
    const basculer = (groupe, cle) => () =>
        setR((p) => ({ ...p, [groupe]: { ...p[groupe], [cle]: !p[groupe]?.[cle] } }));
    const rempli = (...cles) => cles.every((c) => String(r[c] ?? "").trim());

    const controle = useMemo(() => valider(r), [r]);
    const refusDe = (champ) => controle.invalides.find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        return refus.motif === "cle" ? t.motifCle : t.motifFormat;
    };

    const mentions = useMemo(() => composerMentions(r, aujourdhui), [r, aujourdhui]);
    const confidentialite = useMemo(() => composerConfidentialite(r, aujourdhui), [r, aujourdhui]);
    const validation = useMemo(() => composerValidation(r, aujourdhui), [r, aujourdhui]);
    const cookies = useMemo(() => verdictCookies(r), [r]);

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };

    /* L'ordre des écrans de l'éditeur dépend de ses réponses : chaque écran
       sait seulement lequel le suit. */
    const apresForme = () => aller(r.forme === "societe" ? "societe" : r.forme === "association" ? "association" : "ei");
    const apresTva = (tva) => aller(tva === "assujetti" ? "numeroTva" : "reglementee");
    const apresHebergeurs = () => {
        if (r.hebergeurs.length > 1) aller("roles");
        else allerCompleterOuSuite();
    };
    const aCompleter = r.hebergeurs.some((h) => {
        const complet = hebergeurComplet(h);
        return !complet.raisonSociale || !complet.adresse || !complet.telephoneFourni;
    });
    const allerCompleterOuSuite = () => aller(aCompleter ? "completer" : "collectes");
    const reglables = Object.keys(r.outils).filter((id) => r.outils[id] && OUTILS_TIERS[id]?.cookies === "reglage");
    const apresOutils = () => aller(reglables.length ? "reglages" : "contactDonnees");
    const apresContact = () => aller(client ? "prestataire" : "resultat");

    /* Choisir un hébergeur, ou le retirer. Le premier porte « tout le site » ;
       à partir du deuxième, l'écran suivant demande ce que fait chacun. */
    const basculerHebergeur = (id) => () =>
        setR((p) => {
            const deja = p.hebergeurs.find((h) => h.id === id);
            if (deja) {
                const reste = p.hebergeurs.filter((h) => h.id !== id);
                // Seul à nouveau, il porte tout le site.
                return { ...p, hebergeurs: reste.length === 1 ? [{ ...reste[0], role: "site" }] : reste };
            }
            if (p.hebergeurs.length >= HEBERGEURS_MAXIMUM) return p;
            const hebergeurs = [...p.hebergeurs, { id, role: "site", raisonSociale: "", adresse: "", telephone: "", pays: "", horsUe: false }];
            // Dès qu'il y en a deux, « tout le site » ne veut plus rien dire.
            if (hebergeurs.length === 2) {
                hebergeurs[0] = { ...hebergeurs[0], role: hebergeurs[0].role === "site" ? "front" : hebergeurs[0].role };
                hebergeurs[1] = { ...hebergeurs[1], role: "back" };
            } else if (hebergeurs.length > 2) {
                hebergeurs[hebergeurs.length - 1].role = "base";
            }
            return { ...p, hebergeurs };
        });
    const poserHebergeur = (rangH, cle) => (valeur) =>
        setR((p) => ({
            ...p,
            hebergeurs: p.hebergeurs.map((h, i) => (i === rangH ? { ...h, [cle]: valeur } : h)),
        }));

    /* Les documents restent en français ; l'interface, elle, suit la langue. */
    const nomOutil = (id) => (lang === "en" && OUTILS_TIERS[id].nomEn) || OUTILS_TIERS[id].nom;
    const poserOutil = (rangO, cle) => (valeur) =>
        setR((p) => ({
            ...p,
            autresOutils: p.autresOutils.map((o, i) => (i === rangO ? { ...o, [cle]: valeur } : o)),
        }));
    const nomHebergeur = (h) => (h.id === "autre" ? t.autreHebergeur : HEBERGEURS[h.id]?.nom || h.id);

    const choisirDocument = (cle) => {
        setVue(cle);
        setCode(false);
        setCopie("");
    };
    const actuel = vue === "validation" ? validation : vue === "confidentialite" ? confidentialite : mentions;
    const html = useMemo(() => documentEnHtml(actuel), [actuel]);

    const copierHtml = async () => {
        if (!actuel) return;
        try {
            await navigator.clipboard.writeText(html);
            setCopie(vue);
            window.setTimeout(() => setCopie(""), 2000);
        } catch {
            // Presse-papiers refusé : le code reste visible sous « Voir le code ».
            setCode(true);
        }
    };

    const telecharger = () =>
        telechargerDocument({
            document: actuel,
            signature: vue === "validation" ? signature : "",
            nomFichier: nomDuFichier(
                vue === "validation" ? "validation-documents-juridiques" : vue === "confidentialite" ? "politique-de-confidentialite" : "mentions-legales",
                {},
            ),
        });

    /* ---------------------------------------------------------------- */
    /* Les écrans                                                        */
    /* ---------------------------------------------------------------- */

    if (etape === "pourQui") {
        return (
            <div>
                <Question
                    key={etape}
                    {...commun}
                    phase={0}
                    titre={t.pourQuiTitre}
                    aide={t.pourQuiAide}
                    choix={[
                        {
                            icone: "user-rounded-linear",
                            titre: t.pourMoi,
                            detail: t.pourMoiDetail,
                            onClick: () => {
                                // Votre dossier ne préremplit que votre propre site.
                                const garde = Object.fromEntries(
                                    ["prenoms", "nom", "voie", "complement", "codePostal", "commune", "email", "telephone"]
                                        .filter((c) => identite?.[c])
                                        .map((c) => [c, identite[c]]),
                                );
                                if (identite?.siret) garde.siren = identite.siret;
                                setR((p) => ({ ...p, ...garde, pourQui: "moi" }));
                                aller("site");
                            },
                        },
                        {
                            icone: "case-round-linear",
                            titre: t.pourClient,
                            detail: t.pourClientDetail,
                            onClick: () => {
                                // L'identité du dossier est celle du freelance : passée
                                // sur « Mon propre site » puis revenue ici, elle serait
                                // publiée comme celle de l'éditeur du site d'un autre.
                                setR((p) => ({
                                    ...p,
                                    ...Object.fromEntries(
                                        ["prenoms", "nom", "voie", "complement", "codePostal", "commune", "email", "telephone", "siren"].map((c) => [c, VIDE[c]]),
                                    ),
                                    pourQui: "client",
                                }));
                                aller("site");
                            },
                        },
                    ]}
                />
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </div>
        );
    }

    if (etape === "site") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={pour(t.siteTitre, t.siteTitreClient)} aide={t.siteAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champNomSite} valeur={r.nomSite} poser={poser("nomSite")} />
                    <Champ
                        libelle={t.champUrl}
                        aide={t.aideUrl}
                        valeur={r.url}
                        poser={poser("url")}
                        erreur={erreurDe("url")}
                        inputMode="url"
                    />
                </div>
                <Avancer pleineLargeur disabled={!rempli("nomSite", "url") || Boolean(refusDe("url"))} onClick={() => aller("forme")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "forme") {
        return (
            <Question
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={pour(t.formeTitre, t.formeTitreClient)}
                aide={t.formeAide}
                choix={[
                    { cle: "ei", icone: "user-id-linear", titre: t.formeEi, detail: t.formeEiDetail },
                    { cle: "societe", icone: "buildings-2-linear", titre: t.formeSociete, detail: t.formeSocieteDetail },
                    { cle: "association", icone: "users-group-rounded-linear", titre: t.formeAssociation, detail: t.formeAssociationDetail },
                ].map((c) => ({
                    ...c,
                    onClick: () => {
                        setR((p) => ({ ...p, forme: c.cle }));
                        aller(c.cle);
                    },
                }))}
            />
        );
    }

    if (etape === "ei") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={pour(t.eiTitre, t.eiTitreClient)} aide={t.eiAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                        <Champ libelle={t.champNom} valeur={r.nom} poser={poser("nom")} />
                    </div>
                    <Champ libelle={t.champNomCommercial} aide={t.aideNomCommercial} valeur={r.nomCommercial} poser={poser("nomCommercial")} />
                    <div>
                        <Segments
                            libelle={t.activiteLibelle}
                            options={[
                                { cle: "liberale", libelle: t.activiteLiberale },
                                { cle: "commerciale", libelle: t.activiteCommerciale },
                                { cle: "artisanale", libelle: t.activiteArtisanale },
                            ]}
                            valeur={r.activite}
                            poser={poser("activite")}
                        />
                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1.5 mb-0">{t.aideActivite}</p>
                    </div>
                    <Champ
                        libelle={t.champSiren}
                        aide={pour(t.aideSiren, t.aideSirenClient)}
                        valeur={r.siren}
                        poser={poser("siren")}
                        erreur={erreurDe("siren")}
                        inputMode="numeric"
                    />
                    {r.activite === "commerciale" && (
                        <Champ libelle={t.champRcsVille} aide={t.aideRcsVille} valeur={r.rcsVille} poser={poser("rcsVille")} />
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("prenoms", "nom", "activite", "siren") ||
                        (r.activite === "commerciale" && !rempli("rcsVille")) ||
                        Boolean(refusDe("siren"))
                    }
                    onClick={() => aller("coordonnees")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "societe") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={pour(t.societeTitre, t.societeTitreClient)} aide={t.societeAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champDenomination} valeur={r.denomination} poser={poser("denomination")} />
                    <Segments
                        libelle={t.formeSocialeLibelle}
                        options={FORMES_SOCIALES.map((f) => ({ cle: f, libelle: f }))}
                        valeur={r.formeSociale}
                        poser={poser("formeSociale")}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champCapital} valeur={r.capital} poser={poser("capital")} erreur={erreurDe("capital")} inputMode="decimal" />
                        <Champ libelle={t.champSiren} aide={pour(t.aideSiren, t.aideSirenClient)} valeur={r.siren} poser={poser("siren")} erreur={erreurDe("siren")} inputMode="numeric" />
                    </div>
                    <Champ libelle={t.champRcsVille} aide={t.aideRcsVille} valeur={r.rcsVille} poser={poser("rcsVille")} />
                    <Champ libelle={t.champRepresentant} aide={t.aideRepresentant} valeur={r.representant} poser={poser("representant")} />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("denomination", "formeSociale", "capital", "siren", "rcsVille", "representant") ||
                        Boolean(refusDe("siren")) ||
                        Boolean(refusDe("capital"))
                    }
                    onClick={() => aller("coordonnees")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "association") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={pour(t.associationTitre, t.associationTitreClient)} aide={t.associationAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champNomAssociation} valeur={r.nomAssociation} poser={poser("nomAssociation")} />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champRna} aide={t.aideRna} valeur={r.rna} poser={poser("rna")} erreur={erreurDe("rna")} />
                        <Champ libelle={t.champSirenAssociation} aide={t.aideSirenAssociation} valeur={r.siren} poser={poser("siren")} erreur={erreurDe("siren")} inputMode="numeric" />
                    </div>
                    <Champ libelle={t.champPresident} aide={t.aidePresident} valeur={r.president} poser={poser("president")} />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("nomAssociation", "rna", "president") || Boolean(refusDe("rna")) || Boolean(refusDe("siren"))}
                    onClick={() => aller("coordonnees")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "coordonnees") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={pour(t.coordonneesTitre, t.coordonneesTitreClient)} aide={t.coordonneesAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champVoie} valeur={r.voie} poser={poser("voie")} />
                    <Champ libelle={t.champComplement} aide={t.aideComplement} valeur={r.complement} poser={poser("complement")} />
                    <div className="grid sm:grid-cols-3 gap-4">
                        <Champ libelle={t.champCodePostal} valeur={r.codePostal} poser={poser("codePostal")} erreur={erreurDe("codePostal")} inputMode="numeric" />
                        <div className="sm:col-span-2">
                            <Champ libelle={t.champCommune} valeur={r.commune} poser={poser("commune")} />
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champTelephone} valeur={r.telephone} poser={poser("telephone")} type="tel" />
                        <Champ libelle={t.champEmail} valeur={r.email} poser={poser("email")} erreur={erreurDe("email")} type="email" />
                    </div>
                    {r.forme === "ei" && (
                        <p className="text-xs text-gray-500 dark:text-lightBlue/70 mb-0">{t.coordonneesAideAdresse}</p>
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("voie", "codePostal", "commune", "telephone", "email") ||
                        Boolean(refusDe("codePostal")) ||
                        Boolean(refusDe("email"))
                    }
                    onClick={() => aller("tva")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "tva") {
        return (
            <Question
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={pour(t.tvaTitre, t.tvaTitreClient)}
                aide={t.tvaAide}
                choix={[
                    { cle: "franchise", icone: "document-text-linear", titre: t.tvaFranchise, detail: t.tvaFranchiseDetail },
                    { cle: "assujetti", icone: "bill-list-linear", titre: t.tvaAssujetti, detail: t.tvaAssujettiDetail },
                ].map((c) => ({
                    ...c,
                    onClick: () => {
                        setR((p) => ({ ...p, tva: c.cle }));
                        apresTva(c.cle);
                    },
                }))}
            />
        );
    }

    if (etape === "numeroTva") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.numeroTvaTitre} aide={pour(t.numeroTvaAide, t.numeroTvaAideClient)}>
                <Champ libelle={t.champNumeroTva} valeur={r.numeroTva} poser={poser("numeroTva")} erreur={erreurDe("numeroTva")} />
                <Avancer pleineLargeur disabled={!rempli("numeroTva") || Boolean(refusDe("numeroTva"))} onClick={() => aller("reglementee")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "reglementee") {
        return (
            <Question
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.reglementeeTitre}
                aide={t.reglementeeAide}
                choix={[
                    { valeur: "non", icone: "check-circle-linear", titre: t.reglementeeNon, detail: t.reglementeeNonDetail },
                    { valeur: "profession", icone: "diploma-linear", titre: t.reglementeeProfession, detail: t.reglementeeProfessionDetail },
                    { valeur: "autorisation", icone: "document-add-linear", titre: t.reglementeeAutorisation, detail: t.reglementeeAutorisationDetail },
                ].map((c) => ({
                    ...c,
                    onClick: () => {
                        setR((p) => ({ ...p, reglementee: c.valeur }));
                        aller(c.valeur === "non" ? "hebergeurs" : c.valeur);
                    },
                }))}
            />
        );
    }

    if (etape === "profession") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.professionTitre} aide={t.professionAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champProfession} valeur={r.profession} poser={poser("profession")} />
                    <Champ libelle={t.champOrdre} valeur={r.ordre} poser={poser("ordre")} />
                    <Champ libelle={t.champPaysTitre} aide={t.aidePaysTitre} valeur={r.paysTitre} poser={poser("paysTitre")} />
                    <Champ libelle={t.champRegles} aide={t.aideRegles} valeur={r.regles} poser={poser("regles")} />
                </div>
                <Avancer pleineLargeur disabled={!rempli("profession", "ordre", "regles")} onClick={() => aller("hebergeurs")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "autorisation") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.autorisationTitre} aide={t.autorisationAide}>
                <div className="space-y-5">
                    <Champ libelle={t.champAutorite} aide={t.aideAutorite} valeur={r.autorite} poser={poser("autorite")} />
                    <Champ libelle={t.champAdresseAutorite} valeur={r.adresseAutorite} poser={poser("adresseAutorite")} />
                </div>
                <Avancer pleineLargeur disabled={!rempli("autorite", "adresseAutorite")} onClick={() => aller("hebergeurs")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "hebergeurs") {
        const plein = r.hebergeurs.length >= HEBERGEURS_MAXIMUM;
        const coche = (id) => r.hebergeurs.some((h) => h.id === id);
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.hebergeursTitre} aide={t.hebergeursAide}>
                <div className="grid sm:grid-cols-2 gap-2">
                    {[...Object.keys(HEBERGEURS), "autre"].map((id) => (
                        <Case
                            key={id}
                            coche={coche(id)}
                            libelle={id === "autre" ? t.autreHebergeur : HEBERGEURS[id].nom}
                            detail={id === "autre" ? "" : (lang === "en" && HEBERGEURS[id].pourEn) || HEBERGEURS[id].pour}
                            onToggle={basculerHebergeur(id)}
                            disabled={plein && !coche(id)}
                        />
                    ))}
                </div>
                {plein && <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">{t.maximumAtteint}</p>}
                <Avancer pleineLargeur disabled={!r.hebergeurs.length} onClick={apresHebergeurs}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "roles") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.rolesTitre} aide={t.rolesAide}>
                <div className="space-y-6">
                    {r.hebergeurs.map((h, i) => (
                        <Segments
                            key={h.id}
                            libelle={nomHebergeur(h)}
                            options={Object.entries(ROLES_HEBERGEMENT)
                                .filter(([cle]) => cle !== "site")
                                .map(([cle, role]) => ({ cle, libelle: role[lang] || role.fr }))}
                            valeur={h.role}
                            poser={poserHebergeur(i, "role")}
                        />
                    ))}
                </div>
                {controle.alertes.includes("roleEnDouble") && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-4 mb-0">{t.alerteRoleEnDouble}</p>
                )}
                <Avancer pleineLargeur onClick={allerCompleterOuSuite}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "completer") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.completerTitre} aide={t.completerAide}>
                <div className="space-y-8">
                    {r.hebergeurs.map((h, i) => {
                        const complet = hebergeurComplet(h);
                        if (complet.raisonSociale && complet.adresse && complet.telephoneFourni) return null;
                        const fiche = HEBERGEURS[h.id];
                        return (
                            <div key={h.id} className="space-y-4">
                                <Intitule>{nomHebergeur(h)}</Intitule>
                                {fiche ? (
                                    <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                                        {remplace(t.telephoneNonPublie, { nom: fiche.nom })}
                                    </p>
                                ) : (
                                    <>
                                        <Champ libelle={t.champRaisonSociale} valeur={h.raisonSociale} poser={poserHebergeur(i, "raisonSociale")} />
                                        <Champ libelle={t.champAdresseHebergeur} valeur={h.adresse} poser={poserHebergeur(i, "adresse")} />
                                        <Champ libelle={t.champPaysHebergeur} valeur={h.pays} poser={poserHebergeur(i, "pays")} />
                                        <Segments
                                            libelle={t.horsUeLibelle}
                                            options={[
                                                { cle: "non", libelle: t.horsUeNon },
                                                { cle: "oui", libelle: t.horsUeOui },
                                            ]}
                                            valeur={h.horsUe ? "oui" : "non"}
                                            poser={(v) => poserHebergeur(i, "horsUe")(v === "oui")}
                                        />
                                    </>
                                )}
                                {!h.telephoneInconnu && (
                                    <Champ libelle={t.champTelephoneHebergeur} valeur={h.telephone} poser={poserHebergeur(i, "telephone")} type="tel" />
                                )}
                                <Case
                                    coche={Boolean(h.telephoneInconnu)}
                                    libelle={t.telephoneInconnu}
                                    detail={t.telephoneInconnuDetail}
                                    onToggle={() =>
                                        setR((p) => ({
                                            ...p,
                                            hebergeurs: p.hebergeurs.map((x, j) =>
                                                j === i ? { ...x, telephoneInconnu: !x.telephoneInconnu, telephone: "" } : x,
                                            ),
                                        }))
                                    }
                                />
                            </div>
                        );
                    })}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={r.hebergeurs.some((h) => {
                        const c = hebergeurComplet(h);
                        return !c.raisonSociale || !c.adresse || !c.telephone || (!c.catalogue && c.horsUe && !c.pays);
                    })}
                    onClick={() => aller("collectes")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "collectes") {
        return (
            <Ecran key={etape} {...commun} phase={3} onRetour={retour} titre={pour(t.collectesTitre, t.collectesTitreClient)} aide={t.collectesAide}>
                <div className="space-y-2">
                    {COLLECTES.map((cle) => (
                        <Case key={cle} coche={Boolean(r.collectes[cle])} libelle={t[`collecte${majuscule(cle)}`]} onToggle={basculer("collectes", cle)} />
                    ))}
                </div>
                <Avancer pleineLargeur onClick={() => aller("outils")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "outils") {
        return (
            <Ecran key={etape} {...commun} phase={3} onRetour={retour} titre={t.outilsTitre} aide={t.outilsAide}>
                <div className="space-y-6">
                    {CATEGORIES.map((categorie) => {
                        const ids = Object.keys(OUTILS_TIERS).filter((id) => OUTILS_TIERS[id].categorie === categorie);
                        if (!ids.length) return null;
                        return (
                            <div key={categorie}>
                                <Intitule>{t[`categorie${majuscule(categorie)}`]}</Intitule>
                                <div className="grid sm:grid-cols-2 gap-2">
                                    {ids.map((id) => (
                                        <Case key={id} coche={Boolean(r.outils[id])} libelle={nomOutil(id)} onToggle={basculer("outils", id)} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    <div>
                        <Case
                            coche={r.autresOutils.length > 0}
                            libelle={t.autreOutil}
                            detail={t.autreOutilDetail}
                            onToggle={() => setR((p) => ({ ...p, autresOutils: p.autresOutils.length ? [] : [{ ...OUTIL_VIDE }] }))}
                        />
                        {r.autresOutils.map((o, i) => (
                            <div key={i} className="mt-4 space-y-4 rounded-xl border border-gray-200/70 dark:border-white/10 p-4">
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Champ libelle={t.champNomOutil} valeur={o.nom} poser={poserOutil(i, "nom")} />
                                    <Champ libelle={t.champSocieteOutil} aide={t.aideSocieteOutil} valeur={o.societe} poser={poserOutil(i, "societe")} />
                                </div>
                                <Champ libelle={t.champUsageOutil} aide={t.aideUsageOutil} valeur={o.usage} poser={poserOutil(i, "usage")} />
                                <Segments
                                    libelle={t.traceursLibelle}
                                    options={[
                                        { cle: "oui", libelle: t.traceursOui },
                                        { cle: "non", libelle: t.traceursNon },
                                    ]}
                                    valeur={o.traceurs === false ? "non" : "oui"}
                                    poser={(v) => poserOutil(i, "traceurs")(v === "oui")}
                                />
                                <Segments
                                    libelle={t.horsUeLibelle}
                                    options={[
                                        { cle: "non", libelle: t.horsUeNon },
                                        { cle: "oui", libelle: t.horsUeOui },
                                    ]}
                                    valeur={o.horsUe ? "oui" : "non"}
                                    poser={(v) => poserOutil(i, "horsUe")(v === "oui")}
                                />
                                {o.horsUe && <Champ libelle={t.champPaysOutil} valeur={o.pays} poser={poserOutil(i, "pays")} />}
                                <button
                                    type="button"
                                    onClick={() => setR((p) => ({ ...p, autresOutils: p.autresOutils.filter((_, j) => j !== i) }))}
                                    className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-gray-500 dark:text-lightBlue/70 hover:text-primary transition-colors duration-200"
                                >
                                    <SolarIcon name="trash-bin-minimalistic-linear" className="h-4 w-4" />
                                    {t.retirerOutil}
                                </button>
                            </div>
                        ))}
                        {r.autresOutils.length > 0 && r.autresOutils.length < AUTRES_OUTILS_MAXIMUM && (
                            <button
                                type="button"
                                onClick={() => setR((p) => ({ ...p, autresOutils: [...p.autresOutils, { ...OUTIL_VIDE }] }))}
                                className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-gray-600 dark:text-lightBlue hover:text-primary transition-colors duration-200"
                            >
                                <SolarIcon name="add-circle-linear" className="h-4 w-4" />
                                {t.ajouterOutil}
                            </button>
                        )}
                    </div>
                </div>
                <Avancer
                    pleineLargeur
                    disabled={r.autresOutils.some((o) => !String(o.nom).trim() || !String(o.societe).trim() || (o.horsUe && !String(o.pays).trim()))}
                    onClick={apresOutils}
                >
                    {Object.values(r.outils).some(Boolean) || r.autresOutils.length ? t.continuer : t.aucunOutil}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "reglages") {
        return (
            <Ecran key={etape} {...commun} phase={3} onRetour={retour} titre={t.reglagesTitre} aide={t.reglagesAide}>
                <div className="space-y-6">
                    {reglables.map((id) => (
                        <div key={id}>
                            <Segments
                                libelle={nomOutil(id)}
                                options={[
                                    { cle: "standard", libelle: t.reglageStandard },
                                    { cle: "exempte", libelle: t.reglageExempte },
                                ]}
                                valeur={r.reglages[id] || "standard"}
                                poser={(v) => setR((p) => ({ ...p, reglages: { ...p.reglages, [id]: v } }))}
                            />
                            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1.5 mb-0">
                                {OUTILS_TIERS[id].conditions?.[lang] || OUTILS_TIERS[id].conditions?.fr}
                            </p>
                        </div>
                    ))}
                </div>
                <Avancer pleineLargeur onClick={() => aller("contactDonnees")}>
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "contactDonnees") {
        return (
            <Ecran key={etape} {...commun} phase={3} onRetour={retour} titre={t.contactDonneesTitre} aide={t.contactDonneesAide}>
                <Champ
                    libelle={t.champEmailDonnees}
                    valeur={r.emailDonnees || r.email}
                    poser={poser("emailDonnees")}
                    erreur={erreurDe("emailDonnees")}
                    type="email"
                />
                <Avancer pleineLargeur disabled={Boolean(refusDe("emailDonnees"))} onClick={apresContact}>
                    {client ? t.continuer : t.voirDocuments}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "prestataire") {
        return (
            <Ecran key={etape} {...commun} phase={3} onRetour={retour} titre={t.prestataireTitre} aide={t.prestataireAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champPrestataireNom} valeur={r.prestataireNom} poser={poser("prestataireNom")} />
                        <Champ libelle={t.champPrestataireUrl} aide={t.aidePrestataireUrl} valeur={r.prestataireUrl} poser={poser("prestataireUrl")} inputMode="url" />
                    </div>
                    <Segments
                        libelle={t.creditLibelle}
                        options={[
                            { cle: "oui", libelle: t.creditOui },
                            { cle: "non", libelle: t.creditNon },
                        ]}
                        valeur={r.creditCreateur === false ? "non" : "oui"}
                        poser={(v) => setR((p) => ({ ...p, creditCreateur: v === "oui" }))}
                    />
                    <Segments
                        libelle={t.signataireCiviliteLibelle}
                        options={[
                            { cle: "madame", libelle: t.madame },
                            { cle: "monsieur", libelle: t.monsieur },
                        ]}
                        valeur={r.signataireCivilite}
                        poser={poser("signataireCivilite")}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champSignataire} aide={t.aideSignataire} valeur={r.signataireClient} poser={poser("signataireClient")} />
                        <Champ libelle={t.champLieuSignature} aide={t.aideLieuSignature} valeur={r.lieuDeSignature} poser={poser("lieuDeSignature")} />
                    </div>
                </div>
                <Avancer pleineLargeur disabled={!rempli("prestataireNom", "signataireClient", "signataireCivilite")} onClick={() => aller("resultat")}>
                    {t.voirDocuments}
                </Avancer>
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    const nomsOutils = (ids) => ids.map(nomOutil);
    const nomsConsentement = [...nomsOutils(cookies.consentement), ...cookies.autres];
    const documents = [
        { cle: "mentions", libelle: t.docMentions },
        { cle: "confidentialite", libelle: t.docConfidentialite },
        ...(client ? [{ cle: "validation", libelle: t.docValidation }] : []),
    ];

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!mentions ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{t.manquesAide}</p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{libelleManque(lang, cle)}</span>
                            </li>
                        ))}
                        {controle.invalides.map(({ champ, motif }) => (
                            <li key={`${champ}-${motif}`} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {libelleManque(lang, champ)}
                                    {" : "}
                                    {motif === "cle" ? t.motifCle : t.motifFormat}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {/* Le verdict d'abord : c'est la seule décision qui reste au lecteur. */}
                    <div className={`rounded-2xl border p-5 ${cookies.bandeau ? "border-[#D04515]/40 bg-[#fff8f4] dark:bg-white/[0.04]" : "border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03]"}`}>
                        <div className="flex items-center gap-2.5 mb-2">
                            <SolarIcon
                                name={cookies.bandeau ? "shield-warning-linear" : "shield-check-linear"}
                                className={`text-xl shrink-0 ${cookies.bandeau ? "text-[#D04515]" : "text-gray-400 dark:text-lightBlue/60"}`}
                            />
                            <div className="font-bold dark:text-white leading-snug">
                                {cookies.bandeau ? t.cookiesBandeauTitre : t.cookiesSansBandeauTitre}
                            </div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">
                            {cookies.bandeau
                                ? remplace(nomsConsentement.length > 1 ? t.cookiesBandeau : t.cookiesBandeauUn, {
                                      outils: enumeration(nomsConsentement, lang),
                                  })
                                : t.cookiesSansBandeau}
                        </p>
                        {cookies.bandeau && (
                            <ul className="mt-3 space-y-2">
                                {[t.cookiesRegle1, t.cookiesRegle2, t.cookiesRegle3, t.cookiesRegle4].map((ligne) => (
                                    <li key={ligne} className="flex gap-3 text-sm leading-6 text-gray-600 dark:text-lightBlue">
                                        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                        <span>{ligne}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {cookies.exemptes.length > 0 && (
                            <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">
                                {remplace(t.cookiesExemptes, { outils: enumeration(nomsOutils(cookies.exemptes), lang) })}
                            </p>
                        )}
                    </div>

                    {controle.alertes.filter((a) => a !== "roleEnDouble").map((a) => (
                        <div key={a} className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mt-4">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">{t[`alerte${majuscule(a)}`]}</p>
                        </div>
                    ))}

                    <div className="mt-8">
                        <Segments
                            options={documents.map((d) => ({ cle: d.cle, libelle: d.libelle }))}
                            valeur={vue}
                            poser={choisirDocument}
                            libelle={t.documentAffiche}
                        />
                    </div>
                    {t.docEnFrancais && <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-3 mb-0">{t.docEnFrancais}</p>}

                    {vue === "validation" && (
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-4 mb-0">{t.validationAide}</p>
                    )}

                    <div className="mt-4">
                        <ApercuDocument document={actuel} signature={vue === "validation" ? signature : ""} />
                    </div>

                    {vue === "validation" && (
                        <div className="mt-5">
                            <PadSignature
                                libelle={t.signatureClient}
                                aide={t.signatureClientAide}
                                effacer={t.effacer}
                                valeur={signature}
                                poser={setSignature}
                            />
                        </div>
                    )}

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        {vue !== "validation" && (
                            <button
                                type="button"
                                onClick={copierHtml}
                                className="flex-1 inline-flex min-h-[48px] items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                            >
                                <SolarIcon name={copie === vue ? "check-circle-linear" : "code-linear"} className="text-lg" />
                                {copie === vue ? t.htmlCopie : t.copierHtml}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={telecharger}
                            className={`${vue === "validation" ? "flex-1 bg-[#D04515] text-white hover:opacity-90" : "border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue hover:border-primary/40 hover:text-primary"} inline-flex min-h-[48px] items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors duration-200`}
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                    </div>

                    {vue !== "validation" && (
                        <div className="mt-3">
                            <button
                                type="button"
                                onClick={() => setCode((v) => !v)}
                                aria-expanded={code}
                                className="inline-flex min-h-[44px] items-center gap-1.5 text-sm text-gray-500 dark:text-lightBlue/70 hover:text-primary transition-colors duration-200"
                            >
                                <SolarIcon name={code ? "alt-arrow-up-linear" : "alt-arrow-down-linear"} className="h-4 w-4" />
                                {code ? t.masquerCode : t.voirCode}
                            </button>
                            {code && (
                                <pre className="mt-2 max-h-80 overflow-auto rounded-xl border border-gray-200/70 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-4 text-xs leading-5 text-gray-700 dark:text-lightBlue whitespace-pre-wrap break-words">
                                    {html}
                                </pre>
                            )}
                        </div>
                    )}

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon name="clipboard-check-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <ul className="space-y-3">
                            {[
                                t.suite1,
                                t.suite2,
                                cookies.bandeau ? t.suite3Bandeau : t.suite3SansBandeau,
                                ...(client ? [t.suite4Client, t.suite5Client] : []),
                            ].map((ligne) => (
                                <li key={ligne} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8">
                        <Encadre icone="shield-linear" titre={t.fondTitre}>
                            <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.fond}</p>
                        </Encadre>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
